import { Component } from "react"
import PropTypes from "prop-types"
import { capitalize, isEmpty } from "lodash"
import { StarRounded, StarBorderRounded, Close } from "@mui/icons-material"
import { Card, CardContent, CardHeader, IconButton, Paper } from "@mui/material"
import SelectedDate from "components/selected-date"
import { Creators as RootCreators } from "modules/portfolio-root/model"
import { DATE, CHART_ITEM, OPTIONAL_CHART_ITEM, PERIOD, DRIFT_VALUES } from "helpers/shapes"
import { SAME_DAY } from "helpers/dates"
import { getToday } from "helpers/types/dates"
import PeriodSelector from "components/period-selector"
import AbsoluteLineChart from "components/charts/absolute-line-chart"
import BandsLineChart from "components/charts/bands-line-chart"
import { percentageFormatter, numberFormatter } from "helpers/formatters/numbers"
import TopComposites from "modules/factor-profile-top-composites"
import Trend from "./components/trend"
import Tooltip from "./components/tooltip"
import NoFactor from "./components/no-factor"
import UnifiedSearch from "modules/unified-search"
import "./style.scss"

class FactorProfile extends Component {
  static propTypes = {
    isOpPro: PropTypes.bool.isRequired,
    factorId: PropTypes.string.isRequired,
    factorLabel: PropTypes.string.isRequired,
    factorCategory: PropTypes.string.isRequired,
    performanceHistory: PropTypes.arrayOf(CHART_ITEM).isRequired,
    normalizedHistory: PropTypes.arrayOf(OPTIONAL_CHART_ITEM).isRequired,
    predictiveRisk: PropTypes.arrayOf(CHART_ITEM).isRequired,
    predictiveRiskDriftValues: DRIFT_VALUES,
    currentNormalized: PropTypes.number,
    period: PERIOD.isRequired,
    date: DATE,
    startDate: DATE.isRequired,
    endDate: DATE.isRequired,
    earliestDate: DATE.isRequired,
    latestDate: DATE.isRequired,
    setDate: PropTypes.func.isRequired,
    setPeriod: PropTypes.func.isRequired,
    resetDate: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    favoriteFactors: PropTypes.object.isRequired,
    toggleFavoriteFactor: PropTypes.func.isRequired,
    modelId: PropTypes.string.isRequired,
    account: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    openSecurityProfile: PropTypes.func.isRequired,
    openFactorProfile: PropTypes.func.isRequired,
    portfolioId: PropTypes.string,
    datesAdjusted: PropTypes.bool.isRequired,
    isIntradayAnalyticsEnabled: PropTypes.bool,
    isEndDateToday: PropTypes.bool,
  }

  customizeOptions = (options) => [{ value: SAME_DAY, label: "Same Day" }, ...options]

  handleResetEndDate = () => {
    const { period } = this.props
    if (period?.startDate) {
      this.props.setPeriod({ startDate: period.startDate, endDate: getToday() })
    }
  }

  setPeriod = change => {
    if (change.startDate) {
      if (change.adjusted.startDate || change.adjusted.endDate) {
        this.props.addNotification("info", null, `The selected dates have been adjusted to the nearest available date range: ${change.adjusted.startDate || change.startDate} to ${change.adjusted.endDate || change.endDate}.`)
      }

      this.props.setPeriod({
        startDate: change.adjusted.startDate || change.startDate,
        endDate: change.adjusted.endDate || change.endDate,
      })
    } else {
      this.props.setPeriod(change)
    }
  }

  handleToggleFavorite = () => {
    const { toggleFavoriteFactor, factorId, modelId } = this.props
    toggleFavoriteFactor(factorId, modelId)
  }

  render() {
    const {
      isOpPro,
      period,
      factorId,
      factorLabel,
      performanceHistory,
      normalizedHistory,
      predictiveRisk,
      predictiveRiskDriftValues,
      date,
      setDate,
      startDate,
      endDate,
      earliestDate,
      latestDate,
      resetDate,
      favoriteFactors,
      modelId,
      account,
      onClose,
      factorCategory,
      openSecurityProfile,
      openFactorProfile,
      portfolioId,
      datesAdjusted,
      isIntradayAnalyticsEnabled,
      isEndDateToday,
    } = this.props

    if (isEmpty(performanceHistory)) {
      return <NoFactor />
    }

    const itemActions = {
      security: {
        mainAction: item => () => openSecurityProfile(item.assetClass, item.ticker, item.ticker, item.exchange),
      },
      factor: {
        mainAction: item => () => openFactorProfile(item.id),
        secondaryAction: item => () => RootCreators.toggleFavoriteFactor(item.id, modelId),
      },
    }

    const subHeader = startDate && <div className="subheader">
      <div>
        <PeriodSelector {...{ periodAdjusted: datesAdjusted, earliestDate, latestDate, endDate, startDate, period, onChange: this.setPeriod, isIntradayEnabled: isIntradayAnalyticsEnabled, isEndDateToday, handleResetOption: isIntradayAnalyticsEnabled ? this.handleResetEndDate : undefined, customizeOptions: isIntradayAnalyticsEnabled ? this.customizeOptions : undefined }} />
        {date < endDate && <SelectedDate date={date} resetHandler={resetDate} performance />}
      </div>
      <div className="descriptors">
        {capitalize(factorCategory)}
      </div>
    </div>

    const header = <>
      <div className="factor-title">
        <IconButton onClick={this.handleToggleFavorite} color="default" size="large">
          {favoriteFactors[factorId]
            ? <StarRounded className="star-icon-favorite" />
            : <StarBorderRounded />}
        </IconButton>
        {factorLabel}
      </div>
      <UnifiedSearch itemsActions={itemActions} account={account} modelId={modelId} hiddenTypes={["researchTopics", "currencies"]} />
      <div className="controls">
        <IconButton onClick={onClose} color="default" size="large">
          <Close />
        </IconButton>
      </div>
    </>

    const makeTooltip = (isOpPro, tooltipId) => {
      if (isOpPro) {
        return <Tooltip
          id={tooltipId}
          performanceHistory={performanceHistory}
          normalizedHistory={normalizedHistory}
        />
      }

      return <Tooltip
        id={tooltipId}
        performanceHistory={performanceHistory}
        normalizedHistory={normalizedHistory}
        predictiveRisk={predictiveRisk}
      />
    }

    return <Card className="modules_factor-profile">
      <CardHeader className="card-header" disableTypography title={header} />
      <CardHeader className="card-subheader" disableTypography title={subHeader} />
      <CardContent className="card-content">
        <Paper>
          <div className="content">
            <div className="history">
              <Trend data={performanceHistory}>
                <AbsoluteLineChart
                  label="cumulative return"
                  tooltip={makeTooltip(isOpPro, "return")}
                  data={performanceHistory}
                  height={150}
                  {...{ date, setDate }}
                  formatter={percentageFormatter}
                />
              </Trend>
              <Trend data={normalizedHistory}>
                <BandsLineChart
                  label="normalized return"
                  tooltip={makeTooltip(isOpPro, "normalized")}
                  data={normalizedHistory}
                  height={150}
                  {...{ date, setDate }}
                  formatter={numberFormatter}
                />
              </Trend>
              {!isOpPro && <Trend data={predictiveRisk}>
                <AbsoluteLineChart
                  label="predictive risk"
                  tooltip={makeTooltip(isOpPro, "risk")}
                  data={predictiveRisk}
                  height={150}
                  {...{ date, setDate }}
                  formatter={percentageFormatter}
                  driftValues={predictiveRiskDriftValues}
                />
              </Trend>}
            </div>
            <div className="details">
              <TopComposites date={date} factorId={factorId} modelId={modelId} portfolioId={portfolioId} />
            </div>
          </div>
        </Paper>
      </CardContent>
    </Card>
  }
}

export default FactorProfile
